import {
  isSnapshotUnavailableError,
  snapshotAccounts,
  type OpenerDescalceSnapshot
} from '../../api/useOpenerDescalce';

/**
 * The FOUR distinct, named states the page can be in — never a single
 * generic "no data" message:
 *   - `snapshot-unavailable` — the endpoint 404ed (sweeper hasn't published
 *     a read yet, or its Redis key expired).
 *   - `unconfigured` — the snapshot loaded, has zero rows, and EVERY
 *     venue-account reports `source: 'unconfigured'` (no venue credentials at
 *     all — the sweeper never had anything to read). An EMPTY account list is
 *     not this state: no account is not the same claim as every account
 *     unconfigured, and it goes to `error` below.
 *   - `error` — the snapshot loaded with zero rows and at least one
 *     account reports `source: 'error'` (a sweep failed and nothing has
 *     recovered yet), OR the fetch itself failed for a reason other than
 *     the 404 snapshot-unavailable contract, OR the fleet came back
 *     UNREADABLE (see below).
 *   - `empty` — the snapshot loaded cleanly with zero rows and no account
 *     is unconfigured/erroring: genuinely no rule holds a position.
 *   - `rows` — there is at least one row to render.
 */
export type DescalceViewState =
  | 'loading'
  | 'snapshot-unavailable'
  | 'unconfigured'
  | 'error'
  | 'empty'
  | 'rows';

export function resolveDescalceViewState(params: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data: OpenerDescalceSnapshot | undefined;
}): DescalceViewState {
  const { isLoading, isError, error, data } = params;

  // `data` wins over a concurrent `isError`. react-query v3 keeps the last
  // successful snapshot in `data` across a FAILED background refetch (a
  // transient poll failure after a prior success does NOT clear it) — so a
  // real render can have `isError: true` and a populated `data` at the same
  // time. In that case the operator's last-known-good rows must keep
  // rendering (the page's own staleness banner is what surfaces "this is
  // old"), never get replaced by a blank error screen. Only fall through to
  // the fetch-level error states below when a snapshot has NEVER loaded.
  if (data) {
    if (data.rows.length > 0) return 'rows';

    const accountStatuses = snapshotAccounts(data);
    // No fleet to reason about — either genuinely none, or a payload whose
    // `accounts` this build cannot read (the pre-venue-accounts backend keys
    // them by name instead of listing them). Both mean the same thing here:
    // the `unconfigured` / `error` sources that decide the states below are
    // unavailable, so `empty` would be the page asserting "no naked legs" off
    // evidence it does not have. On this screen that claim is the dangerous
    // one, so an unreadable fleet fails toward `error` instead.
    if (accountStatuses.length === 0) return 'error';
    if (accountStatuses.every(a => a.source === 'unconfigured')) {
      return 'unconfigured';
    }
    if (accountStatuses.some(a => a.source === 'error')) return 'error';
    return 'empty';
  }

  if (isError) {
    return isSnapshotUnavailableError(error) ? 'snapshot-unavailable' : 'error';
  }
  return isLoading ? 'loading' : 'empty';
}
